import {
  AfterContentInit,
  Component,
  contentChildren,
  effect,
  input,
  inject,
  model,
  output,
  signal,
  ViewEncapsulation,
  OnDestroy,
  computed
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SelectionChangeEvent } from '../../domain/selectable.domain';
import { ValueAccessorProviderFactory } from '../value-accessor/value-accessor-provider-factory';
import { IdService } from '../../services/id.service';
import { RadioComponent, TD_WB_RADIO } from './radio.component';
import type { InputSignal, ModelSignal, OutputEmitterRef, OutputRefSubscription, Signal } from '@angular/core';
import type { ControlValueAccessor } from '@angular/forms';
import type { Direction } from '../../domain/direction';

/**
 * Dual-support control shape: `value` is the single source of truth and doubles as the
 * Angular Signal Forms native custom-control binding. The `ControlValueAccessor` below is a
 * thin adapter required only by `[(ngModel)]` / `[formControl]`.
 *
 * See `wiki/signal-forms/core-signal-forms-revised-dual-support-plan.md`.
 */
@Component({
  selector: 'td-wb-radio-group',
  templateUrl: 'radio-group.component.html',
  styleUrls: ['radio-group.component.scss'],
  providers: [ValueAccessorProviderFactory(RadioGroupComponent)],
  encapsulation: ViewEncapsulation.None
})
export class RadioGroupComponent implements ControlValueAccessor, OnDestroy, AfterContentInit {
  private static readonly ID_PREFIX: string = 'td-wb-radio';

  private readonly idService = inject(IdService);

  // Inputs
  readonly name: InputSignal<string | undefined> = input<string | undefined>();
  readonly label: InputSignal<string | undefined> = input<string | undefined>();
  readonly ariaLabel: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-label' });
  readonly ariaLabelledBy: InputSignal<string | undefined> = input<string | undefined>(undefined, { alias: 'aria-labelledby' });
  readonly direction: InputSignal<Direction> = input<Direction>('horizontal');

  /** Single source of truth, and the Signal Forms native custom-control binding. */
  readonly value: ModelSignal<unknown> = model<unknown>(undefined);

  /** Signal Forms native custom-control touch notification. */
  readonly touch: OutputEmitterRef<void> = output<void>();

  // Children
  readonly radios: Signal<ReadonlyArray<RadioComponent>> = contentChildren(TD_WB_RADIO, { descendants: true });
  private readonly radios$ = toObservable(this.radios);

  //Derived
  readonly isVertical: Signal<boolean> = computed((): boolean => this.direction() === 'vertical');

  // CVA
  private readonly disabledByOwner = signal(false);
  private onChangeCallback: (value: unknown) => void = (): void => {};
  private onTouchCallback: () => void = (): void => {};

  // Subscription lifecycle
  private readonly destroy: Subject<void> = new Subject<void>();
  private radioSubs: Array<OutputRefSubscription> = [];

  constructor() {
    // Imperative push of derived state into projected child components. This is the
    // sanctioned use of effect(): syncing signal state to a non-signal (imperative) API.
    // It covers writes arriving through the `[(value)]` model binding; `writeValue()` and
    // selection events sync synchronously so callers observe children immediately.
    effect((): void => {
      this.updateSelectedRadioFromValue(this.radios());
      this.applyDisabledState(this.radios());
    });
  }

  ngAfterContentInit(): void {
    this.radios$.pipe(takeUntil(this.destroy)).subscribe((radios: ReadonlyArray<RadioComponent>): void => {
      this.clearRadioSubscriptions();

      if (radios.length === 0) {
        return;
      }

      this.updateRadioButtonRadioIds(radios);
      this.updateRadioButtonNames(radios);
      this.updateSelectedRadioFromValue(radios);
      this.applyDisabledState(radios);

      this.radioSubs = radios.map((radio: RadioComponent): OutputRefSubscription =>
        radio.onSelectionChange.subscribe((event: SelectionChangeEvent<RadioComponent>): void => {
          this.value.set(event.source.value());

          this.updateSelectedRadioFromValue(this.radios());

          if (event.fromView) {
            this.onTouchCallback();
            this.onChangeCallback(this.value());
            this.touch.emit();
          }
        })
      );
    });
  }

  // ControlValueAccessor: owner -> control. Must never emit onChangeCallback.
  writeValue(value: unknown): void {
    this.value.set(value);
    this.updateSelectedRadioFromValue(this.radios());
  }

  registerOnChange(onChangeCallback: (value: unknown) => void): void {
    this.onChangeCallback = onChangeCallback;
  }

  registerOnTouched(onTouchedCallback: () => void): void {
    this.onTouchCallback = onTouchedCallback;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByOwner.set(isDisabled);
    this.applyDisabledState(this.radios());
  }

  ngOnDestroy(): void {
    this.clearRadioSubscriptions();
    this.destroy.next();
    this.destroy.complete();
  }

  /** Re-applied whenever the projected radios change so late-arriving radios stay in sync. */
  private applyDisabledState(radios: ReadonlyArray<RadioComponent>): void {
    const isDisabled = this.disabledByOwner();
    radios.forEach((radio: RadioComponent): void => radio.disabled.set(isDisabled));
  }

  private clearRadioSubscriptions(): void {
    this.radioSubs.forEach((subscription: OutputRefSubscription) => subscription.unsubscribe());
    this.radioSubs = [];
  }

  private readonly generateGroupName: string = this.getRadioId();

  private updateRadioButtonNames(radios: ReadonlyArray<RadioComponent>): void {
    const explicit: string | undefined = this.name();
    const effective: string = (explicit !== undefined && explicit !== '') ? explicit : this.generateGroupName;
    radios.forEach((radio: RadioComponent) =>{
      radio.setNameFromGroup(effective);
    });
  }

  private updateRadioButtonRadioIds(radios: ReadonlyArray<RadioComponent>): void {
    radios.forEach((radio: RadioComponent) => {
      const rid: string | undefined = radio.finalRadioId();
      if (rid === undefined || rid === '') {
        radio.setRadioIdFromGroup(this.getRadioId());
      }
    });
  }

  private getRadioId(): string {
    return this.idService.generateNamespaceSequentialId(RadioGroupComponent.ID_PREFIX);
  }

  private updateSelectedRadioFromValue(radios: ReadonlyArray<RadioComponent>): void {
    const currentValue: unknown = this.value();

    radios.forEach((radio: RadioComponent): void => {
      radio.setCheckedFromGroup(currentValue === radio.value());
    });
  }
}
